import type { ReactNode } from 'react';
import {
  ArchiveRestore,
  BarChart3,
  Boxes,
  Building2,
  Calculator,
  ChevronLeft,
  ClipboardCheck,
  FileInput,
  LayoutDashboard,
  LogOut,
  Menu,
  PackageCheck,
  RefreshCw,
  Search,
  X,
} from 'lucide-react';
import type { User } from '../types';
import { useState } from 'react';
import { Button } from './UI';

export type PageKey = 'dashboard' | 'intake' | 'assets' | 'handover' | 'inventory' | 'allocation' | 'areas' | 'reports';

const navItems: Array<{ key: PageKey; label: string; icon: typeof LayoutDashboard }> = [
  { key: 'dashboard', label: 'Tổng quan', icon: LayoutDashboard },
  { key: 'intake', label: 'Tiếp nhận', icon: FileInput },
  { key: 'assets', label: 'Tài sản & CCDC', icon: Boxes },
  { key: 'handover', label: 'Cấp phát & Thu hồi', icon: ArchiveRestore },
  { key: 'inventory', label: 'Kiểm kê', icon: ClipboardCheck },
  { key: 'allocation', label: 'Phân bổ & Khấu hao', icon: Calculator },
  { key: 'areas', label: 'Khu vực văn phòng', icon: Building2 },
  { key: 'reports', label: 'Báo cáo', icon: BarChart3 },
];

const pageTitles: Record<PageKey, { title: string; subtitle: string }> = {
  dashboard: { title: 'Tổng quan vận hành', subtitle: 'Theo dõi nhanh tài sản, bàn giao và các công việc cần xử lý.' },
  intake: { title: 'Tiếp nhận từ chi nhánh', subtitle: 'Nhận nhanh theo lô, kiểm đếm và tự động tạo hồ sơ tài sản.' },
  assets: { title: 'Tài sản & công cụ dụng cụ', subtitle: 'Tra cứu toàn bộ vật dụng theo mã, tình trạng và khu vực sử dụng.' },
  handover: { title: 'Cấp phát & thu hồi', subtitle: 'Lập checklist, xác nhận người nhận và cập nhật vị trí tự động.' },
  inventory: { title: 'Kiểm kê', subtitle: 'Đối chiếu tài sản thực tế tại từng khu vực văn phòng.' },
  allocation: { title: 'Phân bổ & khấu hao', subtitle: 'Theo dõi giá trị đã phân bổ, còn lại và chạy kỳ hàng tháng.' },
  areas: { title: 'Khu vực văn phòng', subtitle: 'Quản lý công cụ dụng cụ theo từng phòng ban và khu vực sử dụng.' },
  reports: { title: 'Báo cáo quản trị', subtitle: 'Xuất dữ liệu phục vụ Admin, Kế toán và kiểm soát nội bộ.' },
};

export function Layout({ page, onNavigate, user, onLogout, onRefresh, loading, children }: {
  page: PageKey;
  onNavigate: (page: PageKey) => void;
  user: User;
  onLogout: () => void;
  onRefresh: () => void;
  loading?: boolean;
  children: ReactNode;
}) {
  const [mobileOpen, setMobileOpen] = useState(false);
  const meta = pageTitles[page];
  const navigate = (key: PageKey) => {
    onNavigate(key);
    setMobileOpen(false);
  };

  return (
    <div className="app-shell">
      <aside className={`sidebar ${mobileOpen ? 'open' : ''}`}>
        <div className="sidebar-brand">
          <img src="/brand/bismart-logo-full.png" alt="Bi'S MART" />
          <button className="icon-button sidebar-close" onClick={() => setMobileOpen(false)} aria-label="Đóng menu"><X size={20} /></button>
        </div>
        <div className="app-name">
          <span className="app-mark"><PackageCheck size={18} /></span>
          <div><b>Asset Control</b><small>Head Office</small></div>
        </div>
        <nav className="sidebar-nav">
          {navItems.map((item) => {
            const Icon = item.icon;
            return (
              <button key={item.key} className={page === item.key ? 'active' : ''} onClick={() => navigate(item.key)}>
                <Icon size={19} /><span>{item.label}</span>
              </button>
            );
          })}
        </nav>
        <div className="sidebar-footer">
          <div className="user-mini">
            <span>{user.name.charAt(0)}</span>
            <div><b>{user.name}</b><small>{user.employeeCode}</small></div>
          </div>
          <button className="icon-button" onClick={onLogout} title="Đăng xuất"><LogOut size={18} /></button>
        </div>
      </aside>

      {mobileOpen && <div className="sidebar-overlay" onClick={() => setMobileOpen(false)} />}

      <main className="main-shell">
        <header className="topbar">
          <button className="icon-button mobile-menu" onClick={() => setMobileOpen(true)} aria-label="Mở menu"><Menu size={22} /></button>
          <div className="topbar-title">
            <h1>{meta.title}</h1>
            <p>{meta.subtitle}</p>
          </div>
          <div className="topbar-actions">
            <div className="top-search"><Search size={17} /><input placeholder="Tìm mã tài sản..." /></div>
            <Button variant="secondary" onClick={onRefresh} disabled={loading}><RefreshCw size={17} className={loading ? 'spin' : ''} /> Làm mới</Button>
          </div>
        </header>
        <div className="page-content">{children}</div>
      </main>

      <nav className="mobile-bottom-nav">
        {navItems.slice(0, 5).map((item) => {
          const Icon = item.icon;
          return <button key={item.key} className={page === item.key ? 'active' : ''} onClick={() => navigate(item.key)}><Icon size={20} /><span>{item.label.replace(' & Thu hồi', '')}</span></button>;
        })}
      </nav>
    </div>
  );
}
